import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { listingsAPI, ordersAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Separator } from '../components/ui/separator';
import { ArrowLeft, ShoppingCart, Shield, CheckCircle, Loader2, MapPin, Building2, Briefcase } from 'lucide-react';
import { toast } from 'sonner';

function CheckoutPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  
  const [formData, setFormData] = useState({
    campaign_name: '',
    campaign_objective: '',
    targeting: { regions: [], industries: [], job_titles: [] },
    agreed_to_terms: false,
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    const load = async () => {
      try {
        const response = await listingsAPI.getById(id);
        setListing(response.data);
        
        const tierName = searchParams.get('tier') || response.data.pricing_tiers[0]?.name;
        const tier = response.data.pricing_tiers.find(function(t) { return t.name === tierName; }) || response.data.pricing_tiers[0];
        setSelectedTier(tier);
        
        setFormData(function(prev) {
          return {
            ...prev,
            targeting: {
              regions: response.data.regions || [],
              industries: response.data.industries || [],
              job_titles: response.data.job_titles || [],
            }
          };
        });
      } catch (error) {
        toast.error('Failed to load listing');
        navigate('/marketplace');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, searchParams, navigate, isAuthenticated]);

  const toggleTargetingOption = function(type, value) {
    setFormData(function(prev) {
      const current = prev.targeting[type] || [];
      const updated = current.includes(value)
        ? current.filter(function(v) { return v !== value; })
        : current.concat([value]);
      return {
        ...prev,
        targeting: { ...prev.targeting, [type]: updated }
      };
    });
  };

  const handleSubmit = async function(e) {
    e.preventDefault();
    
    if (!formData.campaign_name) {
      toast.error('Please enter a campaign name');
      return;
    }
    if (!formData.agreed_to_terms) {
      toast.error('Please agree to the terms');
      return;
    }
    
    setSubmitting(true);
    try {
      const orderData = {
        listing_id: listing.id,
        quantity: selectedTier.quantity,
        selected_tier: selectedTier.name,
        targeting: formData.targeting,
        campaign_name: formData.campaign_name,
        campaign_objective: formData.campaign_objective,
      };
      
      const response = await ordersAPI.create(orderData);
      toast.success('Order placed successfully!');
      navigate('/buyer/orders/' + response.data.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to place order');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !listing) {
    return (
      <Layout showFooter={false}>
        <div className="container mx-auto px-4 lg:px-8 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-slate-200 rounded w-1/4" />
            <div className="h-64 bg-slate-200 rounded" />
          </div>
        </div>
      </Layout>
    );
  }

  const regions = listing.regions || [];
  const industries = listing.industries || [];
  const jobTitles = listing.job_titles || [];
  const pricingTiers = listing.pricing_tiers || [];
  const targetingRegions = formData.targeting.regions || [];
  const targetingIndustries = formData.targeting.industries || [];
  const targetingJobTitles = formData.targeting.job_titles || [];

  return (
    <Layout showFooter={false}>
      <div className="container mx-auto px-4 lg:px-8 py-8" data-testid="checkout-page">
        <Button variant="ghost" className="mb-6" onClick={function() { navigate(-1); }}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Complete Your Order</h1>
              <p className="text-slate-600">Configure your campaign and targeting preferences</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Campaign Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="campaign_name">Campaign Name *</Label>
                    <Input
                      id="campaign_name"
                      placeholder="e.g., Q1 2024 - IT Security Outreach"
                      value={formData.campaign_name}
                      onChange={function(e) { setFormData({ ...formData, campaign_name: e.target.value }); }}
                      required
                      data-testid="campaign-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="campaign_objective">Campaign Objective (Optional)</Label>
                    <Textarea
                      id="campaign_objective"
                      placeholder="Describe your campaign goals..."
                      value={formData.campaign_objective}
                      onChange={function(e) { setFormData({ ...formData, campaign_objective: e.target.value }); }}
                      rows={3}
                      data-testid="campaign-objective-input"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Targeting Selection</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <div className="flex items-center text-sm font-medium text-slate-700 mb-3">
                      <MapPin className="w-4 h-4 mr-2" />
                      Regions
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {regions.map(function(region) {
                        const isSelected = targetingRegions.includes(region);
                        return (
                          <Badge
                            key={region}
                            variant={isSelected ? 'default' : 'outline'}
                            className="cursor-pointer"
                            onClick={function() { toggleTargetingOption('regions', region); }}
                          >
                            {isSelected && <CheckCircle className="w-3 h-3 mr-1" />}
                            {region}
                          </Badge>
                        );
                      })}
                    </div>
                  </div>

                  <Separator />

                  <div>
                    <div className="flex items-center text-sm font-medium text-slate-700 mb-3">
                      <Building2 className="w-4 h-4 mr-2" />
                      Industries
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {industries.map(function(industry) {
                        const isSelected = targetingIndustries.includes(industry);
                        return (
                          <Badge
                            key={industry}
                            variant={isSelected ? 'default' : 'outline'}
                            className="cursor-pointer"
                            onClick={function() { toggleTargetingOption('industries', industry); }}
                          >
                            {isSelected && <CheckCircle className="w-3 h-3 mr-1" />}
                            {industry}
                          </Badge>
                        );
                      })}
                    </div>
                  </div>

                  <Separator />

                  <div>
                    <div className="flex items-center text-sm font-medium text-slate-700 mb-3">
                      <Briefcase className="w-4 h-4 mr-2" />
                      Job Titles
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {jobTitles.map(function(title) {
                        const isSelected = targetingJobTitles.includes(title);
                        return (
                          <Badge
                            key={title}
                            variant={isSelected ? 'default' : 'outline'}
                            className="cursor-pointer"
                            onClick={function() { toggleTargetingOption('job_titles', title); }}
                          >
                            {isSelected && <CheckCircle className="w-3 h-3 mr-1" />}
                            {title}
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-amber-200 bg-amber-50">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                      <div className="font-medium text-amber-900 mb-1">Compliance Disclaimer</div>
                      <p className="text-sm text-amber-800 mb-4">
                        By placing this order, you confirm that you will use the leads in compliance with GDPR 
                        and other applicable data protection regulations.
                      </p>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="terms"
                          checked={formData.agreed_to_terms}
                          onCheckedChange={function(checked) { setFormData({ ...formData, agreed_to_terms: checked }); }}
                          data-testid="terms-checkbox"
                        />
                        <Label htmlFor="terms" className="text-sm text-amber-800 cursor-pointer">
                          I agree to the terms and confirm GDPR compliance
                        </Label>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Button 
                type="submit" 
                size="lg" 
                className="w-full"
                disabled={submitting || !formData.agreed_to_terms}
                data-testid="place-order-btn"
              >
                {submitting ? (
                  <span className="flex items-center">
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Placing Order...
                  </span>
                ) : (
                  <span className="flex items-center">
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    Place Order - ${selectedTier?.price.toLocaleString()}
                  </span>
                )}
              </Button>
            </form>
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <Card className="shadow-medium">
                <CardHeader>
                  <CardTitle className="text-lg">Order Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Badge variant="outline" className="mb-2">{listing.category}</Badge>
                    <h3 className="font-semibold text-slate-900">{listing.title}</h3>
                    <p className="text-sm text-slate-500">{listing.company_name}</p>
                  </div>

                  <Separator />

                  <div className="space-y-2">
                    <Label className="text-sm text-slate-600">Package</Label>
                    <div className="space-y-2">
                      {pricingTiers.map(function(tier) {
                        const isSelected = selectedTier?.name === tier.name;
                        return (
                          <div
                            key={tier.name}
                            className={'p-3 rounded-lg border cursor-pointer transition-all ' + (isSelected ? 'border-sky-500 bg-sky-50' : 'border-slate-200 hover:border-slate-300')}
                            onClick={function() { setSelectedTier(tier); }}
                          >
                            <div className="flex justify-between">
                              <div>
                                <div className="font-medium text-sm">{tier.name}</div>
                                <div className="text-xs text-slate-500">{tier.quantity} leads</div>
                              </div>
                              <div className="text-right">
                                <div className="font-semibold">${tier.price.toLocaleString()}</div>
                                <div className="text-xs text-slate-500">${tier.cpl}/lead</div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <Separator />

                  {selectedTier && (
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Package</span>
                        <span className="font-medium">{selectedTier.name}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Leads</span>
                        <span className="font-medium">{selectedTier.quantity}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Delivery</span>
                        <span className="font-medium">{listing.delivery_sla_days} days</span>
                      </div>
                    </div>
                  )}

                  <Separator />

                  <div className="flex justify-between text-lg">
                    <span className="font-semibold">Total</span>
                    <span className="font-bold">${selectedTier?.price.toLocaleString()}</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default CheckoutPage;
